import React, { useState, useEffect } from "react";
import { Flex, Divider, Text, Select } from "@theorgchartdev/storybook";
import { appstr } from "@coreapp/utils/appstr";
import { DoValidationAlert, createIdResolver } from "./RecordEditor.utils";
import {
  kPositionID,
  kParentPositionID,
  kPositionLocation,
  kPositionStartDate,
  kPositionEndDate,
  kPositionTitleID,
  kPositionDivisionID,
  kDesiredHolders,
  kPositionCostBandMinimum,
  kPositionCostBandMaximum,
  enumOverrideType,
  enumObjectTypes,
  kPositionStatusID,
  PositionStatus,
  kPositionBudgetedSalary
} from "@coreapp/constants";

import BasicInfoSection from "./Components/BasicInfoSection";
import BaseRecordEditorModal from "./BaseRecordEditorModal";
import { QObjDBObject } from "@coreapp/CoreApp/QObjDBObject";
import { QChange } from "@coreapp/CoreApp/QChange";
import { TOAST_STATUS, useToast } from "@/providers/ToastProvider";
import { createClickableToast } from "@/appComponents/Blocks/Cards/ToastContainer/utils/createClickableToast";
import { createPositionClickHandler } from "@coreapp/utils/clickableToastHandlers";
import {
  BuildPositionFieldMap,
  BuildPositionRangeFieldsMap,
  createPositionOverride as createPositionOverrideUtil,
  loadPositionById,
  isUserMappedField,
  isCostBandMapped,
  calculateTotalPositionBudget,
  getPositionBudgetTooltip
} from "./PositionEditorUtils";
import { NumericInputField } from "@/appComponents/Blocks/Composites/ReactInputFields/NumericInputField/NumericInputField";
import { sanitizeBlockedValue } from "@coreapp/utils/FormatFieldValue";

/**
 * Create a new position override and position object
 */
const DEFAULT_FIELD_MAP = {
  [kPositionTitleID]: "",
  [kPositionID]: "",
  [kParentPositionID]: "",
  [kDesiredHolders]: "",
  [kPositionLocation]: "",
  [kPositionDivisionID]: "",
  [kPositionStartDate]: "",
  [kPositionEndDate]: "",
  [kPositionBudgetedSalary]: "",
  [kPositionCostBandMinimum]: "",
  [kPositionCostBandMaximum]: "",
  [kPositionStatusID]: ""
};

const CreatePositionModal = ({
  theData,
  showCreatePosition,
  showClonePosition,
  onSaveRecordEdit,
  onClosePositionEditor,
  parentPosition,
  manager,
  queryChartId = null
}) => {
  const { addToast } = useToast();

  const initialWidth = 900;
  const initialHeight = 625;

  const createPositionOverride = () => {
    const override = createPositionOverrideUtil(enumOverrideType.add, manager);
    if (override) {
      override.fChanges = [];
    }
    return override;
  };

  // Create a new position object and override to use with BaseRecordEditorModal
  const [positionOverride, setPositionOverride] = useState(() => createPositionOverride());

  const [errors, setErrors] = useState([]);
  const [values, setValues] = useState({});
  const [copyCount, setCopyCount] = useState("1");
  const [customCopyCount, setCustomCopyCount] = useState("1");
  const [isCustomCount, setIsCustomCount] = useState(false);
  const [isInitialized, setIsInitialized] = useState(false);
  const [selectedSupervisor, setSelectedSupervisor] = useState(null);

  const getInitialPositionId = () => {
    const positionClass = manager?.GetDoc()?.GetPositionClass();

    if (!positionClass) {
      return "NEW_000001";
    }

    if (!manager) {
      const additionalObjects = parentPosition ? [parentPosition] : [];
      return positionClass?.GenerateUniqueRecordID(additionalObjects) || "NEW_000001";
    }

    // Get all existing add overrides for positions
    const addOverrides = manager.GetChangesOfType(enumOverrideType.add);
    const positionAddOverrides = addOverrides.filter(
      override => override.SmartGetFieldValue("objType") === enumObjectTypes.position
    );

    // Generate unique ID considering parent and all existing added positions
    const additionalObjects = parentPosition
      ? [parentPosition, ...positionAddOverrides]
      : positionAddOverrides;
    return positionClass.GenerateUniqueRecordID(additionalObjects);
  };

  const initializeChangeValue = () => {
    let initialValues = {};
    if (showCreatePosition) {
      initialValues = {
        [kPositionTitleID]: "",
        [kPositionID]: getInitialPositionId(),
        [kParentPositionID]: parentPosition?.SmartGetFieldValue("PositionID") || "",
        [kDesiredHolders]: "1",
        [kPositionLocation]: parentPosition?.SmartGetFieldValue("Position Location") || "",
        [kPositionDivisionID]: parentPosition?.SmartGetFieldValue("Position Division") || "",
        [kPositionStartDate]: new Date().toISOString().split("T")[0],
        [kPositionEndDate]: "",
        [kPositionBudgetedSalary]: "",
        [kPositionCostBandMinimum]: "",
        [kPositionCostBandMaximum]: "",
        [kPositionStatusID]: PositionStatus.Active
      };
    } else if (showClonePosition && parentPosition) {
      initialValues = Object.keys(DEFAULT_FIELD_MAP).reduce((acc, fieldId_) => {
        acc[fieldId_] = parentPosition.SmartGetFieldValue(fieldId_) || "";
        return acc;
      }, {});

      initialValues[kPositionID] = getInitialPositionId();
    }

    return initialValues;
  };

  useEffect(() => {
    const shouldInitialize =
      (showCreatePosition && !isInitialized) ||
      (showClonePosition && parentPosition && !isInitialized);

    if (shouldInitialize) {
      const initialValues = initializeChangeValue();
      setValues(initialValues);

      if (!positionOverride) {
        const newOverride = createPositionOverride();
        setPositionOverride(newOverride);
      }

      if (showCreatePosition && parentPosition) {
        setSelectedSupervisor(parentPosition);
      } else if (showClonePosition && parentPosition) {
        const parentOfParent = parentPosition.GetParent();
        if (parentOfParent) {
          setSelectedSupervisor(parentOfParent);
        } else {
          // If GetParent() returns null, try to load parent from the position's parent field
          const parentPositionID = parentPosition.SmartGetFieldValue(kParentPositionID);
          if (parentPositionID) {
            loadPositionById(parentPositionID, loadedParent => {
              setSelectedSupervisor(loadedParent);
            });
          }
        }
      }

      setIsInitialized(true);
    }
  }, [parentPosition, showCreatePosition, showClonePosition, isInitialized, positionOverride]);

  const [fieldNameToIdMap, setFieldNameToIdMap] = useState({});

  // Use useEffect to set up the field mapping once
  useEffect(() => {
    const positionClass = manager?.GetDoc()?.GetPositionClass();

    if (positionClass) {
      // Map each field name to its corresponding ID constant
      const map = Object.keys(DEFAULT_FIELD_MAP).reduce((acc, fieldId_) => {
        const fieldDef = positionClass.GetFieldDefByID(fieldId_);
        if (fieldDef) {
          acc[fieldDef.GetFieldName()] = fieldId_ || "";
        }
        return acc;
      }, {});
      setFieldNameToIdMap(map);
    }
  }, [showCreatePosition, showClonePosition]);

  if (!(showCreatePosition || showClonePosition) || !positionOverride || !manager) {
    return null;
  }

  if (showClonePosition && !parentPosition) {
    return null;
  }

  const GetPositionDetails = () => {
    if (showCreatePosition) return { title: values["Position Title"], id: values["PositionID"] };

    return {
      title: sanitizeBlockedValue(parentPosition.SmartGetFieldValue("Position Title")),
      id: sanitizeBlockedValue(parentPosition.SmartGetFieldValue("PositionID"))
    };
  };

  // Predefined copy count options with clickHandlers
  const getCopyCountOptions = (setIsCustomCount, setCopyCount) => [
    {
      value: "1",
      label: "1",
      clickHandler: () => {
        setIsCustomCount(false);
        setCopyCount("1");
      }
    },
    {
      value: "2",
      label: "2",
      clickHandler: () => {
        setIsCustomCount(false);
        setCopyCount("2");
      }
    },
    {
      value: "3",
      label: "3",
      clickHandler: () => {
        setIsCustomCount(false);
        setCopyCount("3");
      }
    },
    {
      value: "4",
      label: "4",
      clickHandler: () => {
        setIsCustomCount(false);
        setCopyCount("4");
      }
    },
    {
      value: "5",
      label: "5",
      clickHandler: () => {
        setIsCustomCount(false);
        setCopyCount("5");
      }
    }
    /*
    // TODO uncomment when better framework in place for mass creation
    {
      value: "custom",
      label: appstr("Custom"),
      clickHandler: () => {
        setIsCustomCount(true);
      }
    }
     */
  ];

  // Handle form validation
  const validateFields = () => {
    const errors = [];

    // Validate required fields
    if (!values[kPositionID] || !values[kPositionID].trim()) {
      errors.push(appstr("PositionID is required"));
    }

    if (!values[kPositionTitleID] || !values[kPositionTitleID].trim()) {
      errors.push(appstr("Position Title is required"));
    }

    if (!values[kParentPositionID]) {
      errors.push(appstr("Parent PositionID is required"));
    }

    // Validate Desired Holders (Allocation)
    const allocation = values[kDesiredHolders];
    if (allocation && (isNaN(allocation) || parseInt(allocation) <= 0)) {
      errors.push(appstr("Allocation must be a positive number"));
    }

    // Validate Cost Band
    const costBandMin = values[kPositionCostBandMinimum];
    const costBandMax = values[kPositionCostBandMaximum];

    if (costBandMin && isNaN(costBandMin)) {
      errors.push(appstr("Cost Band Minimum must be a number"));
    }

    if (costBandMax && isNaN(costBandMax)) {
      errors.push(appstr("Cost Band Maximum must be a number"));
    }

    if (costBandMin && costBandMax && parseFloat(costBandMin) > parseFloat(costBandMax)) {
      errors.push(appstr("Cost Band Minimum cannot be greater than Cost Band Maximum"));
    }

    // Validate dates
    const startDate = values[kPositionStartDate] ? new Date(values[kPositionStartDate]) : null;
    const endDate = values[kPositionEndDate] ? new Date(values[kPositionEndDate]) : null;

    if (startDate && endDate && startDate > endDate) {
      errors.push(appstr("End Date must be on or after Start Date"));
    }

    // Validate copy count
    const actualCopyCount = isCustomCount ? customCopyCount : copyCount;
    if (actualCopyCount && (isNaN(actualCopyCount) || parseInt(actualCopyCount) <= 0)) {
      errors.push(appstr("Copy count must be a positive number"));
    }

    return errors;
  };

  // Updated handleFieldChange function
  const handleFieldChange = ({ field, value }) => {
    // Look up the corresponding field ID for the given field name
    const fieldId = fieldNameToIdMap[field] || field;

    setValues(prevValues => {
      const newValues = {
        ...prevValues,
        [fieldId]: value
      };
      const isPositionBudgetMapped = isUserMappedField(kPositionBudgetedSalary);
      const isCostBandsMapped = isCostBandMapped(kPositionCostBandMaximum);

      if (!isPositionBudgetMapped && isCostBandsMapped) {
        const costBandMax =
          fieldId === kPositionCostBandMaximum ? value : prevValues[kPositionCostBandMaximum];
        const desiredHolders = fieldId === kDesiredHolders ? value : prevValues[kDesiredHolders];

        const derivedValue = calculateTotalPositionBudget(
          () => 0,
          () => parseFloat(costBandMax) || 0,
          () => parseInt(desiredHolders) || 1
        );

        newValues[kPositionBudgetedSalary] = derivedValue.toString();
      }

      return newValues;
    });
  };
  // Search select handler for supervisor field
  const searchClickHandler = target => {
    // Extract field name from target - it might be passed differently from BasicInfoSection
    const fieldName = target.field || target.fieldName;
    const fieldId = fieldNameToIdMap[fieldName] || fieldName || kParentPositionID;

    setValues(prevValues => ({
      ...prevValues,
      [fieldId]: target.value
    }));

    // Update selected supervisor if it's the parent position field
    if (fieldId === kParentPositionID && target.result) {
      // Create a QObjDBObject from the result
      const positionClass = manager?.GetDoc()?.GetPositionClass();

      if (positionClass) {
        const supervisorObj = new QObjDBObject();
        supervisorObj.InitObject(positionClass);
        positionClass.ProcessObject(supervisorObj, target.result);
        setSelectedSupervisor(supervisorObj);
      }
    }
  };

  // Handler for removing parent position
  const handleRemoveParentPosition = () => {
    setValues(prevValues => ({
      ...prevValues,
      [kParentPositionID]: ""
    }));
    setSelectedSupervisor(null);
  };

  // Handle save action with position creation
  const handleSave = async () => {
    const validationErrors = validateFields();
    if (validationErrors.length > 0) {
      setErrors(validationErrors);
      DoValidationAlert(validationErrors);
      return { success: false };
    }

    // Get the actual copy count
    let actualCopyCount = isCustomCount ? parseInt(customCopyCount) : parseInt(copyCount);
    if (isNaN(actualCopyCount)) actualCopyCount = 1;

    // Create position objects for saving
    const overrideClass = manager?.fOverrideClass;

    if (!manager?.fWSMgr || !manager || !overrideClass) {
      addToast({
        message: appstr("Unable to Create Positions"),
        status: TOAST_STATUS.ERROR
      });
      return { success: false };
    }

    const positionClass = manager?.GetDoc()?.GetPositionClass();

    if (!positionClass) {
      addToast({
        message: appstr("Unable to Create Positions"),
        status: TOAST_STATUS.ERROR
      });
      return { success: false };
    }

    // Get the field definitions for the proper field names
    const positionIDName = positionClass.GetFieldDefByID(kPositionID)?.GetFieldName();
    const parentPositionIDName = positionClass.GetFieldDefByID(kParentPositionID)?.GetFieldName();
    const titleDef = positionClass.GetFieldDefByID(kPositionTitleID)?.GetFieldName();
    const locationDef = positionClass.GetFieldDefByID(kPositionLocation)?.GetFieldName();
    const divisionDef = positionClass.GetFieldDefByID(kPositionDivisionID)?.GetFieldName();
    const startDateDef = positionClass.GetFieldDefByID(kPositionStartDate)?.GetFieldName();
    const endDateDef = positionClass.GetFieldDefByID(kPositionEndDate)?.GetFieldName();
    const desiredHoldersDef = positionClass.GetFieldDefByID(kDesiredHolders)?.GetFieldName();
    const costBandMinDef = positionClass.GetFieldDefByID(kPositionCostBandMinimum)?.GetFieldName();
    const costBandMaxDef = positionClass.GetFieldDefByID(kPositionCostBandMaximum)?.GetFieldName();
    const statusName = positionClass.GetFieldDefByID("PositionStatus")?.GetFieldName();
    const budgetedSalary =
      positionClass.GetFieldDefByID(kPositionBudgetedSalary)?.GetFieldName() ||
      kPositionBudgetedSalary;

    const overrides = [];
    const createdPositions = [];

    const basePositionId = values[kPositionID] || "";
    const actionID = actualCopyCount > 1 ? crypto.randomUUID() : null;

    // Create the position overrides
    for (let i = 0; i < actualCopyCount; i++) {
      // Create a new position object to be the 'placeholder' for the override
      const positionObj = new QObjDBObject();
      positionObj.InitObject(positionClass);

      const uniquePositionId =
        actualCopyCount === 1
          ? values[kPositionID] || ""
          : createIdResolver(basePositionId, positionClass, [
              ...(parentPosition ? [parentPosition] : []),
              ...createdPositions
            ])();

      // Prepare position data - this will be applied through the override change
      const positionData = {
        [titleDef]: values[kPositionTitleID] || "",
        [parentPositionIDName]: values[kParentPositionID] || "",
        [desiredHoldersDef]: values[kDesiredHolders] || "1",
        [locationDef]: values[kPositionLocation] || "",
        [divisionDef]: values[kPositionDivisionID] || "",
        [startDateDef]: values[kPositionStartDate] || null,
        [endDateDef]: values[kPositionEndDate] || null,
        [costBandMinDef]: values[kPositionCostBandMinimum] || "",
        [costBandMaxDef]: values[kPositionCostBandMaximum] || "",
        [positionIDName]: uniquePositionId,
        [statusName]: values[kPositionStatusID] || PositionStatus.Active,
        [budgetedSalary]: values[kPositionBudgetedSalary] ?? values[budgetedSalary] ?? ""
      };

      positionObj.SmartSetFieldValue(kPositionID, positionData[positionIDName]);
      positionObj.SmartSetFieldValue(kParentPositionID, positionData[parentPositionIDName]);

      // Create the override object for this position
      const positionOverrideClone = positionOverride.Clone(true);
      positionOverrideClone.SmartSetFieldValue("Type", enumOverrideType.add);
      positionOverrideClone.SmartSetFieldValue(
        "RefID",
        positionObj.SmartGetFieldValue(kPositionID)
      );
      positionOverrideClone.SmartSetFieldValue("override_id", null);

      // Create and init the change object with the position data
      const positionChangeDict = {
        author: manager.fWSMgr.GetUserName(),
        date: new Date().toUTCString(),
        actionID,
        data: positionData
      };

      // Create and populate the change
      const positionChange = new QChange();
      positionChange.InitChange(
        manager.fWSMgr,
        positionChangeDict,
        positionOverrideClone,
        positionObj,
        overrideClass
      );
      positionChange.UpdateChange(positionChangeDict, enumOverrideType.add);

      // Add the change to the override
      positionOverrideClone.fChanges = [];
      positionOverrideClone.fChanges.push(positionChange);

      // Add the override to our list
      overrides.push(positionOverrideClone);
      createdPositions.push(positionObj);
    }

    // Send all the overrides to create the positions
    let successful = false;
    try {
      const response = await manager.SendUpdate(overrides, false, false, false);
      const result = JSON.parse(response);

      if (result["success"]) {
        successful = true;

        if (createdPositions.length > 0) {
          const positionTitle = values[kPositionTitleID];
          const positionId = result.navToRecord;

          let positionEntity = `${positionTitle}: ${result.navToRecord}`;
          if (actualCopyCount > 1) {
            const additionalCount = actualCopyCount - 1;
            const andMoreText = appstr("and {count} more").replace("{count}", additionalCount);
            positionEntity += ` ${andMoreText}`;
          }

          addToast({
            content: createClickableToast({
              template: "Create position success",
              key: "entity",
              value: { entity: positionEntity },
              options: {
                keySpecificHandlers: { entity: createPositionClickHandler(positionId) }
              },
              textProps: { colorModifier: "success" }
            }),
            status: TOAST_STATUS.SUCCESS,
            timeoutMS: 10000
          });
        } else {
          // Fallback if no position objects available
          addToast({
            message: appstr("Create position success"),
            status: TOAST_STATUS.SUCCESS
          });
        }

        // Reset the form after successful creation
        closeEditor();
      }
    } catch (error) {
      console.error("Error creating positions:", error);
    }

    if (!successful) {
      addToast({
        message: appstr("Unable to create positions"),
        status: TOAST_STATUS.ERROR
      });
    }

    return { success: successful };
  };

  // Reset the form to initial state
  const closeEditor = () => {
    // Reset to default values
    const initialValues = initializeChangeValue();
    setValues(initialValues);

    // Reset copy count options
    setCopyCount("1");
    setCustomCopyCount("1");
    setIsCustomCount(false);
    setErrors([]);
    setIsInitialized(false);
    setPositionOverride(null);
    setSelectedSupervisor(null);
    onClosePositionEditor();
  };

  // Handle copy count change
  const handleCopyCountChange = value => {
    if (value === "custom") {
      setIsCustomCount(true);
    } else {
      setIsCustomCount(false);
      setCopyCount(value);
    }
  };

  // Get parent of parent position for clone mode
  const parentOfParentPosition = parentPosition?.GetParent();

  // Build field maps for BasicInfoSection using the shared utility
  const fieldValuesMap = BuildPositionFieldMap({
    values,
    errors,
    isCreateMode: true,
    onRemoveParentPosition: handleRemoveParentPosition,
    parentPosition: showCreatePosition ? parentPosition : parentOfParentPosition,
    recordSupervisor: selectedSupervisor,
    manager,
    queryChartId
  });

  const isPositionBudgetMapped = isUserMappedField(kPositionBudgetedSalary);
  const isCostBandsMapped = isCostBandMapped(kPositionCostBandMaximum);

  if (!isPositionBudgetMapped && isCostBandsMapped) {
    const salaryField = fieldValuesMap[kPositionBudgetedSalary];
    if (salaryField) {
      const tooltip = getPositionBudgetTooltip(parentPosition || null);
      salaryField.textEditOptions = {
        ...(salaryField.textEditOptions || {}),
        enableTE: false,
        inputProps: {
          ...(salaryField.textEditOptions?.inputProps || {}),
          disabled: true,
          labelInfo: tooltip
        }
      };
      fieldValuesMap[kPositionBudgetedSalary] = salaryField;
    }
  }

  // Build range fields map using the shared utility
  const rangeFieldsMap = BuildPositionRangeFieldsMap({
    values,
    errors,
    isCreateMode: true,
    manager
  });

  // Define modal header title
  const getHeaderTitle = () => {
    return showCreatePosition ? appstr("Create position") : appstr("Clone position");
  };

  // If modal should not be shown, return null
  if (!manager || !(showCreatePosition || showClonePosition)) {
    return null;
  }

  const copyCountOptions = getCopyCountOptions(setIsCustomCount, setCopyCount);

  // Dummy controller data for BaseRecordEditorModal since we handle state independently
  const controllerData = {
    record: parentPosition,
    baseRecord: null,
    recordSupervisor: null,
    override: positionOverride ? { state: positionOverride } : null,
    existingSupervisorID: null,
    changeResults: values,
    changeType: enumOverrideType.add,
    madeFieldReverts: false,
    workingOverride: positionOverride
  };

  const handleUnsavedChanges = () => {
    const defaultValue = initializeChangeValue();
    const currentValue = { ...values };
    // compare currentValue with defaultValue
    return Object.keys(currentValue).some(key => {
      return currentValue[key] !== defaultValue[key];
    });
  };

  const handleRevertOverride = () => {};
  const handleInitializeTarget = () => {};
  const handleLoadRecordFallback = () => {};

  const getDynamicTextProps = () => {
    const template = appstr("How many position copies");
    const replaceDict = GetPositionDetails();
    const formatConfig = {
      title: { type: "bold" },
      id: { type: "bold" }
    };

    return {
      template,
      replaceDict,
      formatConfig
    };
  };

  const requiredTicker = <span className={"ocn-form-label-required"}> *</span>;

  return (
    <BaseRecordEditorModal
      show={true}
      showComments={false}
      overrideObj={positionOverride}
      dbObj={parentPosition}
      type={enumOverrideType.add}
      onSave={onSaveRecordEdit}
      onClose={closeEditor}
      manager={manager}
      dataTestIdPrefix={"create-position-"}
      isPlanEdit={true}
      size={{ width: initialWidth, height: initialHeight }}
      otherHeaderProps={{
        size: "xlarge",
        labelSize: "large"
      }}
      otherOverlayProps={{ labelSize: "large" }}
      validateFields={validateFields}
      getHeaderTitle={getHeaderTitle}
      onSaveWithChanges={handleSave}
      loadRecord={false} // Important! Disable loading record to prevent supervisor loading
      controllerData={controllerData}
      onUnsavedChanges={handleUnsavedChanges}
      onRevertOverride={handleRevertOverride}
      onInitializeTarget={handleInitializeTarget}
      onLoadRecordFallback={handleLoadRecordFallback}
      requireDbObj={false}
    >
      <Flex direction={"column"} gap={"16px"} padding={"0"}>
        {/* Copy Position Section */}
        <Flex direction={"column"} gap={"8px"} width={"60%"} padding={"0 0 0 16px"}>
          <Flex gap={"4px"}>
            <Text
              dynamic={showClonePosition ? { ...getDynamicTextProps() } : undefined}
              size={"small"}
            >
              {showClonePosition
                ? appstr("How many position copies", GetPositionDetails())
                : appstr("How many new position copies")}
            </Text>
            {requiredTicker}
          </Flex>
          <Select
            selectSize={"medium"}
            inputOnly
            value={isCustomCount ? "custom" : copyCount}
            onChange={handleCopyCountChange}
            options={copyCountOptions}
          />
        </Flex>
        {isCustomCount && (
          <div style={{ marginBottom: "16px" }}>
            <NumericInputField
              label={appstr("Custom count")}
              value={customCopyCount}
              allowDummyFieldDef
              allowNegative={false}
              returnNumber
              onChange={(_, value) => setCustomCopyCount(value)}
              isAllowed={values => {
                const { floatValue } = values;
                if (floatValue === undefined) return true; // Allow empty value
                return floatValue >= 1 && floatValue <= 25;
              }}
              size={"medium"}
              min={1}
            />
          </div>
        )}
        <Divider />
        <BasicInfoSection
          parentObject={selectedSupervisor}
          fieldValuesMap={fieldValuesMap}
          rangeFieldsMap={rangeFieldsMap}
          onFieldChange={handleFieldChange}
          onSearchSelect={searchClickHandler}
          dataTestIdPrefix="create-position"
          queryChartId={queryChartId}
        />
      </Flex>
    </BaseRecordEditorModal>
  );
};

export default CreatePositionModal;
